import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api, descargar } from "../lib/api";
import { diasHasta, creditos, fecha, plural } from "../lib/formato";
import {
  Aviso,
  Cabecera,
  Cargando,
  EstadoSocio,
  FotoSocio,
  Panel,
  Vacio,
} from "../componentes/piezas";
import { useSesion } from "../lib/sesion";
import { useIdioma, type Variables } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

interface Fila {
  id: string;
  numeroSocio: number;
  nombre: string;
  apellidos: string;
  documentoNumero: string;
  telefono: string | null;
  estado: string;
  fechaAlta: string;
  fechaCaducidad: string | null;
  saldoCentimos: number;
  fotoUrl: string | null;
  tipoSocio: { id: string; nombre: string; color: string } | null;
  fechaNacimiento: string;
  aportadoCentimos: number;
  ultimaVisita: string | null;
}

interface TipoSocio {
  id: string;
  nombre: string;
}

const ESTADOS = ["ACTIVO", "PENDIENTE", "CADUCADO", "SUSPENDIDO", "BAJA"];

/** Los años que tiene hoy. */
function anios(nacimiento: string) {
  const n = new Date(nacimiento);
  const hoy = new Date();
  let a = hoy.getFullYear() - n.getFullYear();
  const m = hoy.getMonth() - n.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < n.getDate())) a -= 1;
  return a;
}

/**
 * «hace 3 días», «hace 2 meses».
 *
 * La fecha sola no dice si un socio esta perdido: hay que restarla de cabeza.
 * Lo que se quiere saber de un vistazo es cuanto hace que no aparece.
 *
 * Recibe el traductor en vez de devolver castellano: el texto de una pantalla
 * no se compone a trozos en una funcion suelta, que es como se acaba con media
 * frase en un idioma y media en otro.
 */
function haceCuanto(
  iso: string,
  t: (clave: Clave, variables?: Variables) => string,
) {
  /*
   * Se cuentan dias de calendario, no milisegundos: una visita de ayer por la
   * noche esta a menos de 24 horas y saldria como «hoy», que es justo lo que
   * el club no quiere leer cuando repasa quien falta por aparecer.
   */
  const soloDia = (f: Date) =>
    Date.UTC(f.getFullYear(), f.getMonth(), f.getDate());
  const dias = Math.round(
    (soloDia(new Date()) - soloDia(new Date(iso))) / 86400000,
  );
  if (dias <= 0) return t("s.hoy");
  if (dias === 1) return t("s.ayer");
  if (dias < 31) return t("s.haceDias", { dias });
  const meses = Math.floor(dias / 30);
  if (meses < 12)
    return meses === 1 ? t("s.haceUnMes") : t("s.haceMeses", { meses });
  const anos = Math.floor(dias / 365);
  return anos === 1 ? t("s.haceUnAnio") : t("s.haceAnios", { anios: anos });
}

export default function Socios() {
  const navegar = useNavigate();
  const { puede } = useSesion();
  const { t, dias: enDias } = useIdioma();
  const [buscar, setBuscar] = useState("");
  const [estado, setEstado] = useState("");
  const [tipoSocioId, setTipoSocioId] = useState("");
  const [visitoDesde, setVisitoDesde] = useState("");
  const [pagina, setPagina] = useState(1);
  const [error, setError] = useState("");
  const porPagina = 25;

  const { data, isLoading } = useQuery({
    queryKey: ["socios", buscar, estado, tipoSocioId, visitoDesde, pagina],
    queryFn: () =>
      api<{ total: number; socios: Fila[] }>("/socios", {
        query: {
          buscar,
          estado: estado || undefined,
          tipoSocioId: tipoSocioId || undefined,
          visitoDesde: visitoDesde || undefined,
          pagina,
          porPagina,
        },
      }),
  });

  const tipos = useQuery({
    queryKey: ["tipos-socio"],
    queryFn: () => api<TipoSocio[]>("/tipos-socio"),
  });

  const paginas = data ? Math.max(1, Math.ceil(data.total / porPagina)) : 1;

  return (
    <>
      <Cabecera
        titulo={t("s.titulo")}
        descripcion={
          data
            ? t("s.enElRegistro", { socios: plural(data.total, "socio") })
            : undefined
        }
      >
        {puede("verInformes") && (
          <button
            className="boton secundario"
            onClick={() =>
              descargar("/exportar/socios.csv", "socios.csv").catch((fallo) =>
                setError(
                  fallo instanceof Error ? fallo.message : t("s.falloExportar"),
                ),
              )
            }
          >
            {t("s.exportar")}
          </button>
        )}
        {puede("verInformes") && (
          <Link className="boton secundario" to="/socios/libro">
            {t("s.libro")}
          </Link>
        )}
        {puede("importarSocios") && (
          <Link className="boton secundario" to="/socios/importar">
            {t("s.importar")}
          </Link>
        )}
        <Link className="boton secundario" to="/socios/carnets">
          {t("s.carnets")}
        </Link>
        {puede("crearSocio") && (
          <Link className="boton" to="/socios/nuevo">
            {t("s.nuevo")}
          </Link>
        )}
      </Cabecera>

      <Aviso tipo="error">{error}</Aviso>

      <Panel sinCuerpo>
        <div className="panel-cabecera">
          <input
            style={{ maxWidth: 340 }}
            placeholder={t("s.buscar")}
            value={buscar}
            onChange={(e) => {
              setBuscar(e.target.value);
              setPagina(1);
            }}
          />
          <select
            style={{ maxWidth: 190 }}
            value={estado}
            onChange={(e) => {
              setEstado(e.target.value);
              setPagina(1);
            }}
          >
            <option value="">{t("s.todosLosEstados")}</option>
            {ESTADOS.map((e) => (
              <option key={e} value={e}>
                {t(`g.estado.${e}` as Clave)}
              </option>
            ))}
          </select>
          <select
            style={{ maxWidth: 190 }}
            value={tipoSocioId}
            onChange={(e) => {
              setTipoSocioId(e.target.value);
              setPagina(1);
            }}
          >
            <option value="">{t("s.cualquierTipo")}</option>
            {(tipos.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.nombre}
              </option>
            ))}
          </select>
          <label
            className="fila pequeno suave"
            style={{ gap: 6, whiteSpace: "nowrap" }}
          >
            {t("s.hanVenidoDesde")}
            <input
              type="date"
              value={visitoDesde}
              onChange={(e) => {
                setVisitoDesde(e.target.value);
                setPagina(1);
              }}
              style={{ width: 150 }}
            />
          </label>
        </div>

        {isLoading ? (
          <Cargando />
        ) : !data || data.socios.length === 0 ? (
          <Vacio>
            {/* Decirle "no coincide con la búsqueda" a un club recién creado
                que no ha buscado nada le hace pensar que hay un filtro puesto
                y ponerse a quitarlo. Lo que le pasa es que aun no tiene socios. */}
            {buscar || estado || tipoSocioId || visitoDesde ? (
              t("s.sinCoincidencias")
            ) : (
              <>
                {t("s.sinSocios")}{" "}
                {puede("crearSocio") && (
                  <Link to="/socios/nuevo">{t("s.darDeAlta")}</Link>
                )}
                {puede("crearSocio") &&
                  puede("importarSocios") &&
                  ` ${t("general.o")} `}
                {puede("importarSocios") && (
                  <Link to="/socios/importar">{t("s.oImportar")}</Link>
                )}
                {(puede("crearSocio") || puede("importarSocios")) && "."}
              </>
            )}
          </Vacio>
        ) : (
          <div className="tabla-envoltorio">
            <table className="tabla">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>{t("s.numero")}</th>
                  <th>{t("s.socio")}</th>
                  <th>{t("s.documento")}</th>
                  <th>{t("s.estado")}</th>
                  <th>{t("s.cuotaHasta")}</th>
                  <th>{t("s.ultimaVisita")}</th>
                  <th className="num">{t("s.aportado")}</th>
                  <th className="num">{t("s.cartera")}</th>
                </tr>
              </thead>
              <tbody>
                {data.socios.map((socio) => {
                  const dias = diasHasta(socio.fechaCaducidad);
                  return (
                    <tr
                      key={socio.id}
                      className="pulsable"
                      onClick={() => navegar(`/socios/${socio.id}`)}
                    >
                      <td className="tabular suave">{socio.numeroSocio}</td>
                      <td>
                        <div className="fila">
                          <FotoSocio socio={socio} />
                          <div>
                            <strong>
                              {socio.nombre} {socio.apellidos}
                            </strong>
                            <div className="suave pequeno">
                              {t("s.anios", {
                                anios: anios(socio.fechaNacimiento),
                              })}{" "}
                              · {socio.telefono ?? t("s.sinTelefono")}
                              {socio.tipoSocio && (
                                <span
                                  className="etiqueta"
                                  style={{
                                    marginLeft: 6,
                                    background: `${socio.tipoSocio.color}20`,
                                    color: socio.tipoSocio.color,
                                  }}
                                >
                                  {socio.tipoSocio.nombre}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="tabular pequeno">
                        {socio.documentoNumero}
                      </td>
                      <td>
                        <EstadoSocio estado={socio.estado} />
                      </td>
                      <td className="pequeno">
                        {fecha(socio.fechaCaducidad)}
                        {dias !== null && dias >= 0 && dias <= 30 && (
                          <div className="suave">
                            {t(dias === 1 ? "s.queda" : "s.quedan", {
                              dias: enDias(dias),
                            })}
                          </div>
                        )}
                      </td>
                      <td className="pequeno">
                        {socio.ultimaVisita ? (
                          <>
                            {fecha(socio.ultimaVisita)}
                            <div className="suave">
                              {haceCuanto(socio.ultimaVisita, t)}
                            </div>
                          </>
                        ) : (
                          <span className="suave">{t("s.nunca")}</span>
                        )}
                      </td>
                      <td className="num tabular">
                        {socio.aportadoCentimos > 0 ? (
                          creditos(socio.aportadoCentimos)
                        ) : (
                          <span className="suave">—</span>
                        )}
                      </td>
                      <td className="num tabular">
                        {socio.saldoCentimos > 0
                          ? creditos(socio.saldoCentimos)
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {paginas > 1 && (
          <div
            className="panel-cabecera"
            style={{ borderTop: "1px solid var(--borde)", borderBottom: 0 }}
          >
            <span className="suave pequeno">
              {t("g.pagina", { pagina, paginas })}
            </span>
            <div className="fila">
              <button
                className="boton secundario"
                disabled={pagina <= 1}
                onClick={() => setPagina((p) => p - 1)}
              >
                {t("g.anterior")}
              </button>
              <button
                className="boton secundario"
                disabled={pagina >= paginas}
                onClick={() => setPagina((p) => p + 1)}
              >
                {t("g.siguiente")}
              </button>
            </div>
          </div>
        )}
      </Panel>
    </>
  );
}
